import { APIClient, HydraUIAPIError } from '@customerio/hydra-ui-api';
import { getApiBaseUrl } from '@/services/api/base-url';
import { sampleWorkspaceFetch } from '@/services/api/sample-workspace-host';
import { getAuthHeaders } from '@/services/api/auth-headers';
import { recordRumRequest } from '@/services/rum';

/** What `GET /v1/environments/{environment_id}/realtime/token` returns. */
interface RealtimeTokenResponse {
  token: string;
  /** WebSocket URL of realtime_api, e.g. `wss://realtime.customer.io/connection/websocket`. */
  url: string;
  /** Unix seconds. */
  expires_at: number;
}

interface RealtimePaths {
  '/v1/environments/{environment_id}/realtime/token': {
    get: {
      parameters: { path: { environment_id: number } };
      responses: { 200: { content: { 'application/json': RealtimeTokenResponse } } };
    };
  };
}

const realtimeClient = new APIClient<RealtimePaths>({
  baseUrl: getApiBaseUrl,
  fetch: sampleWorkspaceFetch,
  fetchOptions: { credentials: 'include' },
  headers: getAuthHeaders,
  onRequestTiming: recordRumRequest,
});

export type RealtimeToken = { token: string; url: string; expiresAt: number };

/** The server will not issue a token (flag off, no access, masquerading), so retrying is pointless. */
export class RealtimeDeniedError extends Error {}

const DENIED_STATUSES = [401, 403, 404];

function validate(response: RealtimeTokenResponse): RealtimeToken {
  if (typeof response.token !== 'string' || response.token === '' || typeof response.url !== 'string') {
    throw new Error('Malformed realtime token response');
  }
  return { token: response.token, url: response.url, expiresAt: response.expires_at };
}

export async function fetchRealtimeToken(environmentId: number): Promise<RealtimeToken> {
  try {
    const response = await realtimeClient.get('/v1/environments/{environment_id}/realtime/token', {
      params: { path: { environment_id: environmentId } },
    });
    return validate(response);
  } catch (error) {
    if (error instanceof HydraUIAPIError && DENIED_STATUSES.includes(error.status)) {
      throw new RealtimeDeniedError(`Realtime token refused (${error.status})`);
    }
    throw error;
  }
}
